/**
 * Payment and Campaign Configuration
 *
 * Source of truth is the backend (backend/src/config.py).
 * For deployments where you want to override these from the build, set
 * the matching REACT_APP_* env var. Defaults below match the backend
 * defaults so they stay in sync if no env override is provided.
 */

const _num = (v, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// Payment split percentages
export const FIXED_PAYMENT_PERCENTAGE = _num(process.env.REACT_APP_FIXED_PAYMENT_PERCENTAGE, 0.60);
export const PERFORMANCE_PAYMENT_PERCENTAGE = _num(process.env.REACT_APP_PERFORMANCE_PAYMENT_PERCENTAGE, 0.40);

// Monitoring duration options shown in the campaign creation form.
export const MONITORING_DURATION_OPTIONS = [
  { value: 1, unit: 'weeks', label: '1 Week', description: 'Quick campaigns, product launches' },
  { value: 2, unit: 'weeks', label: '2 Weeks', description: 'Standard campaigns (Recommended)', recommended: true },
  { value: 3, unit: 'weeks', label: '3 Weeks', description: 'Extended tracking for bigger campaigns' },
  { value: 4, unit: 'weeks', label: '4 Weeks', description: 'Maximum monitoring (monthly campaigns)' }
];
export const DEFAULT_MONITORING_WEEKS = 2;
export const MAX_MONITORING_WEEKS = 4;

// Content submission
export const MAX_CONTENT_REVISIONS = 3;

// Kill fee (when brand cancels after creator submitted content).
//
// Split policy:
//   - 8.5% of the creator's fixed payment is deducted from the brand's escrow.
//   - 3.5% of fixed → platform cancellation fee (immediate platform revenue).
//   - 5.0% of fixed → creator's net payout (what they actually receive).
//
// ``KILL_FEE_PERCENTAGE`` continues to mean the TOTAL deduction for any
// existing callers; new code should prefer the explicit *_TOTAL_/_PLATFORM_/
// _CREATOR_NET_ constants below.
export const KILL_FEE_TOTAL_PERCENTAGE = 0.085;
export const KILL_FEE_PLATFORM_CUT_PERCENTAGE = 0.035;
export const KILL_FEE_CREATOR_NET_PERCENTAGE = 0.05;
export const KILL_FEE_PERCENTAGE = KILL_FEE_TOTAL_PERCENTAGE;

// Platform fees
// Additive fee model: the brand pays creator gross + 3.5% platform fee at
// finalize time. Creators receive the FULL contracted amount; the platform
// fee is paid on top by the brand, NOT deducted from creator payouts.
export const PLATFORM_FEE_PERCENTAGE = _num(process.env.REACT_APP_PLATFORM_FEE_PERCENTAGE, 0.035);

// Payment gateway approximate fee
export const PAYMENT_GATEWAY_FEE_PERCENTAGE = _num(process.env.REACT_APP_PAYMENT_GATEWAY_FEE_PERCENTAGE, 0.0236);

// Refund logic by campaign stage
// Note: Platform fee (3.5%) is always non-refundable
export const REFUND_STAGES = {
  BEFORE_ACCEPTANCE: {
    description: 'Before any creator accepts',
    refund: 'Full creator allocation refunded (platform fee non-refundable)'
  },
  AFTER_ACCEPTANCE: {
    description: 'After acceptance, before work starts',
    refund: 'Full creator allocation refunded (platform fee non-refundable)'
  },
  CONTENT_SUBMITTED: {
    kill_fee_total: 0.085,           // 8.5% of fixed deducted from brand's escrow
    kill_fee_creator_net: 0.05,      // 5%   to creator
    kill_fee_platform_cut: 0.035,    // 3.5% retained as platform cancellation fee
    description: 'Content submitted but not approved',
    refund: 'Allocation minus kill fee (8.5%) refunded — creator receives 5%, platform retains 3.5%'
  },
  CONTENT_APPROVED: {
    description: 'Content approved - cannot cancel',
    refund: 'N/A - Campaign cannot be cancelled'
  },
  IN_MONITORING: {
    description: 'Cannot cancel during monitoring'
  },
  COMPLETED: {
    description: 'Campaign completed, no refund'
  }
};

// Helper functions
export const calculatePlatformFee = (amount) => {
  return Math.round(amount * PLATFORM_FEE_PERCENTAGE * 100) / 100;
};

export const calculateKillFee = (fixedPayment) => {
  // Returns the TOTAL kill-fee deduction from brand's escrow (8.5% of fixed).
  // This is what the brand sees as "kill fee deducted" on their refund.
  return Math.round(fixedPayment * KILL_FEE_TOTAL_PERCENTAGE * 100) / 100;
};

export const calculateKillFeeNet = (fixedPayment) => {
  // Returns the creator's NET kill-fee payout (5% of fixed) — what they
  // actually receive when the admin disburses.
  return Math.round(fixedPayment * KILL_FEE_CREATOR_NET_PERCENTAGE * 100) / 100;
};

export const calculateKillFeePlatformCut = (fixedPayment) => {
  // Returns the platform's cancellation fee (3.5% of fixed) — retained
  // immediately at cancel time, separate from the upfront 3.5% platform fee.
  return Math.round(fixedPayment * KILL_FEE_PLATFORM_CUT_PERCENTAGE * 100) / 100;
};

export const calculateTotalWithPlatformFee = (creatorCost) => {
  // Total brand pays = creator cost + platform fee
  const platformFee = calculatePlatformFee(creatorCost);
  return creatorCost + platformFee;
};

export const calculateCreatorAllocationFromEscrow = (escrowAmount) => {
  // Extract creator allocation from escrow (which includes platform fee)
  return Math.round(escrowAmount / (1 + PLATFORM_FEE_PERCENTAGE) * 100) / 100;
};

// Per-week payment calculation for the monitoring period.
// Additive fee model: creator receives the full weekly amount. Platform fee
// was collected upfront from the brand and is not deducted per-payment.
export const calculateWeeklyPayment = (totalFixed, totalPerformance, monitoringWeeks) => {
  const periods = Math.max(1, monitoringWeeks || DEFAULT_MONITORING_WEEKS);
  const weeklyFixedGross = totalFixed / periods;

  return {
    weeklyFixedGross,
    weeklyFixedPlatformFee: 0,
    weeklyFixedNet: weeklyFixedGross,
    totalWeeks: periods
  };
};

// What the creator actually receives. Under the additive fee model the
// creator gets the full gross — the platform fee was already collected from
// the brand at finalize time and is NOT deducted again.
export const calculateCreatorNetPayment = (grossAmount) => {
  const rounded = Math.round(grossAmount * 100) / 100;
  return {
    gross: rounded,
    platformFee: 0,
    net: rounded
  };
};

// Calculate full payment breakdown for a creator.
// Additive fee model: creator receives the full gross at every step.
export const calculateCreatorPaymentBreakdown = (creatorPrice, monitoringWeeks, performancePercent = 100) => {
  const periods = Math.max(1, monitoringWeeks || DEFAULT_MONITORING_WEEKS);
  const fixedGross = creatorPrice * FIXED_PAYMENT_PERCENTAGE;
  const performanceGross = (creatorPrice * PERFORMANCE_PAYMENT_PERCENTAGE * performancePercent) / 100;

  const weeklyFixedGross = fixedGross / periods;

  return {
    fixed: {
      gross: Math.round(fixedGross * 100) / 100,
      platformFee: 0,
      net: Math.round(fixedGross * 100) / 100,
      weeklyGross: Math.round(weeklyFixedGross * 100) / 100,
      weeklyNet: Math.round(weeklyFixedGross * 100) / 100
    },
    performance: {
      gross: Math.round(performanceGross * 100) / 100,
      platformFee: 0,
      net: Math.round(performanceGross * 100) / 100
    },
    total: {
      gross: Math.round((fixedGross + performanceGross) * 100) / 100,
      platformFee: 0,
      net: Math.round((fixedGross + performanceGross) * 100) / 100
    }
  };
};
